import { ComponentProps, PropsWithChildren } from 'react';
import {
  FlexColumn, FormConfigurationProvider, PageRefName, useRefs,
} from '@equifinance/shared';
import styled from 'styled-components';
import AccountTabNav, { ApplicationPageType } from '../AccountTabNav';
import Page from '.';
import { PageConfigurationProvider, usePageConfigurationContext } from '../../contexts/pageConfiguration';
import usePageAccessControl from '../../hooks/usePageAccessCtrl';

const Container = styled(FlexColumn)`
  width:100%;
`;

const Icon = styled.i`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[7]};
`}`;

const BackToTopButton = styled.div`
  position: absolute;
  z-index: 5;
  bottom: 100px;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-top-left-radius: ${({ theme }) => theme.borderRadius[2]};
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius[2]};
  padding: ${({ theme }) => theme.padding[2]};
  background: ${({ theme }) => theme.colours.neutral[7]};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize[4]};
  color: ${({ theme }) => theme.colours.neutral[2]};
  cursor: pointer;
`;

type AccountTabPageProps = ComponentProps<typeof Page> & { page: ApplicationPageType };

export function FormConfigurationAdapter({ children }: PropsWithChildren) {
  const { isReadOnly } = usePageConfigurationContext();
  return (
    <FormConfigurationProvider isReadOnlyInput={isReadOnly}>
      {children}
    </FormConfigurationProvider>
  );
}

export default function AccountTabPage(props: AccountTabPageProps) {
  const { children, page, ...pageProps } = props;
  const { refs } = useRefs();

  const backToTop = () => {
    refs[PageRefName]?.current?.scrollTo({ top: 0, behavior: 'instant' });
  };

  usePageAccessControl(page);

  return (
    <PageConfigurationProvider page={page}>
      <FormConfigurationAdapter>
        <Page {...pageProps}>
          <Container>
            <AccountTabNav page={page} />
            {children}
            <BackToTopButton onClick={backToTop}>
              <Icon className="mi-arrow-up" />
              Back to top
            </BackToTopButton>
          </Container>
        </Page>
      </FormConfigurationAdapter>
    </PageConfigurationProvider>
  );
}
